import { useEffect, useState } from 'react';
import type { VoidFunctionComponent } from 'react';

import LicenseDrawer from '..';
import Locator from '../../../../../shared/Locator';
import { Action } from '../../../../../typings/permissions';
import { useDresser } from '../../../../contexts/DrawerContext';
import useCorrectLicense from '../../../../hooks/api/licenses/useCorrectLicense';
import useEditable from '../../../../hooks/useEditable';
import useOnMount from '../../../../hooks/useOnMount';
import useHasProjectPermission from '../../../../shared/permissions/useHasProjectPermission';
import Button, { BUTTON_TYPE, ButtonGroup, TooltipButton } from '../../../generic/Button';
import Flex from '../../../generic/Flex';
import FullContentTextArea from '../../../generic/Forms/FullContentTextArea';
import type { DependencyLicense } from '../../../../../shared/types/licenses';
import type { DependencyProp } from '../types';

import styles from '../LicenseDrawer.module.less';

type Props = {
  licenseText: DependencyLicense['licenseText'];
  revisionLicenseId: number;
  dependency: DependencyProp;
};

type ModeContentProps = Props & {
  setIsEditing: (isEditing: boolean) => void;
  setText: (text: string) => void;
};

const EditingModeLicenseTextTab: VoidFunctionComponent<ModeContentProps> = ({
  licenseText,
  dependency,
  setIsEditing,
  revisionLicenseId,
  setText,
}) => {
  const { replaceDrawer } = useDresser();
  const { data, isDirty, onUpdate, onSave } = useEditable(licenseText ?? '');
  const { mutate: correctLicense, data: correctLicenseData } = useCorrectLicense(dependency.locator, revisionLicenseId);

  useEffect(() => {
    if (correctLicenseData?.data?.result && correctLicenseData.data?.result?.id !== revisionLicenseId) {
      replaceDrawer(
        <LicenseDrawer
          scope="dependency"
          revisionLicenseId={correctLicenseData.data.result.id}
          dependency={dependency}
        />,
      );
    }
  }, [correctLicenseData, dependency, replaceDrawer, revisionLicenseId]);

  return (
    <>
      <div className={styles.title}>Editing License</div>
      <FullContentTextArea
        label="License Text"
        value={data}
        onChange={onUpdate}
      />
      <Flex justify="space-between" direction="row-reverse">
        <ButtonGroup>
          <Button type={BUTTON_TYPE.SECONDARY} onClick={() => setIsEditing(false)}>
            Cancel
          </Button>
          <Button
            onClick={() => {
              correctLicense({ data: { text: data } });
              setText(data);
              onSave();
              setIsEditing(false);
            }}
            isDisabled={!isDirty}
          >
            Save
          </Button>
        </ButtonGroup>
      </Flex>
    </>
  );
};

const ReadModeLicenseTextTab: VoidFunctionComponent<Omit<ModeContentProps, 'revisionLicenseId' | 'setText'>> = ({
  licenseText,
  dependency,
  setIsEditing,
}) => {
  const hasPermissionToEdit = useHasProjectPermission(Locator.parsePackageString(dependency?.locator), Action.Edit);
  return (
    <>
      <div className={styles.title}>
        <Flex justify="space-between">
          Full License Text
          <TooltipButton
            id="edit-license-text"
            onClick={() => setIsEditing(true)}
            isDisabled={!hasPermissionToEdit}
            tooltip="You do not have permission to edit this project."
            placement="left"
            showTooltip={!hasPermissionToEdit}
          >
            Edit
          </TooltipButton>
        </Flex>
      </div>
      <FullContentTextArea label="Copyright" value={licenseText ?? ''} isDisabled />
    </>
  );
};

const LicenseTextTab: VoidFunctionComponent<Props> = (props) => {
  const { setWidthPercent } = useDresser();
  useOnMount(
    () => setWidthPercent(100),
    () => setWidthPercent(undefined),
  );

  const [isEditing, setIsEditing] = useState(false);
  const [text, setText] = useState(props.licenseText);

  if (isEditing) {
    return (
      <EditingModeLicenseTextTab
        licenseText={text}
        dependency={props.dependency}
        setIsEditing={setIsEditing}
        revisionLicenseId={props.revisionLicenseId}
        setText={setText}
      />
    );
  }

  return (
    <ReadModeLicenseTextTab licenseText={text} dependency={props.dependency} setIsEditing={setIsEditing} />
  );
};

export default LicenseTextTab;
